import {
  PORTAL_EVENT_TYPES,
  preparePortalEventPayload,
  recordPortalEventSafely,
} from './portalEventsClient';

const recordedExposureTokens = new Set();
const EXPOSURE_STORAGE_KEY = 'portalExperimentExposures:v1';
const MAX_EXPOSURE_ATTEMPTS = 3;
const pendingExposures = new Map();
let exposureGeneration = 0;

const wasExposureRecorded = (assignmentId) => {
  if (recordedExposureTokens.has(assignmentId)) return true;
  try {
    const stored = JSON.parse(
      window.sessionStorage.getItem(EXPOSURE_STORAGE_KEY) || '[]',
    );
    return Array.isArray(stored) && stored.includes(assignmentId);
  } catch (_error) {
    return false;
  }
};

const rememberExposure = (assignmentId) => {
  recordedExposureTokens.add(assignmentId);
  try {
    const stored = JSON.parse(
      window.sessionStorage.getItem(EXPOSURE_STORAGE_KEY) || '[]',
    );
    const next = new Set(Array.isArray(stored) ? stored : []);
    next.add(assignmentId);
    window.sessionStorage.setItem(
      EXPOSURE_STORAGE_KEY,
      JSON.stringify([...next]),
    );
  } catch (_error) {}
};

const retryDelay = (attempt) =>
  new Promise((resolve) => {
    setTimeout(resolve, 500 * 2 ** attempt);
  });

export const recordExperimentExposure = (assignment) => {
  const assignmentId = String(assignment?.assignmentId || '').trim();
  const assignmentToken = String(assignment?.assignmentToken || '').trim();

  if (
    assignment?.participating !== true ||
    !assignmentId ||
    !assignment?.experimentKey ||
    !assignment?.variant
  ) {
    return Promise.resolve(false);
  }

  if (wasExposureRecorded(assignmentId)) return Promise.resolve(true);
  if (!assignmentToken) return Promise.resolve(false);
  const pendingExposure = pendingExposures.get(assignmentId);
  if (pendingExposure) return pendingExposure;

  const generation = exposureGeneration;
  const exposureEvent = {
    eventType: PORTAL_EVENT_TYPES.experimentExposed,
    experimentAssignmentToken: assignmentToken,
  };
  let preparedPayload;
  try {
    preparedPayload = preparePortalEventPayload(exposureEvent);
  } catch (_error) {
    return Promise.resolve(false);
  }
  const persistExposure = async () => {
    for (let attempt = 0; attempt < MAX_EXPOSURE_ATTEMPTS; attempt += 1) {
      const persisted = await recordPortalEventSafely(exposureEvent, {
        preparedPayload,
      });
      if (generation !== exposureGeneration) return false;
      if (persisted) {
        rememberExposure(assignmentId);
        return true;
      }
      if (attempt < MAX_EXPOSURE_ATTEMPTS - 1) {
        await retryDelay(attempt);
        if (generation !== exposureGeneration) return false;
      }
    }
    return false;
  };

  const promise = persistExposure().finally(() => {
    if (pendingExposures.get(assignmentId) === promise) {
      pendingExposures.delete(assignmentId);
    }
  });
  pendingExposures.set(assignmentId, promise);
  return promise;
};

export const clearExperimentExposureHistory = () => {
  exposureGeneration += 1;
  recordedExposureTokens.clear();
  pendingExposures.clear();
  try {
    window.sessionStorage.removeItem(EXPOSURE_STORAGE_KEY);
  } catch (_error) {}
};
